import { Deserializer } from "jsonapi-serializer";
import { CC_RECIPIENT, DAILY_OPTION, FILTER_BUILDINGS, FILTER_DEVICE_TYPE, FILTER_DOORS, FILTER_EVENTS, FILTER_SITES, FILTER_TIME_ZONE, FILTER_ZONES, MONTHLY_OPTION, MSG_BODY, MSG_DISCLAIMER, MSG_SUBJECT, PATTERN, PATTERN_DAY_OF_MONTH, PATTERN_DAY_OF_WEEK, PATTERN_DAY_TYPE, PATTERN_EVERY_DAY, PATTERN_EVERY_MONTH, PATTERN_EVERY_WEEK, PATTERN_EVERY_YEAR, PATTERN_MONTH_OF_YEAR, RANGE_END_DATE, RANGE_OCCURRENCE, RANGE_START_DATE, RECURRENCE, START_TIME, TO_RECIPIENT, VRULE_TRIGGERS_IDS, YEARLY_OPTION } from '../constants/notification-key';
import { CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT, OK } from "../constants/response-status-codes";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { genericError } from '../models/GenericError';
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { Notification } from '../models/Notification';
import { NotificationActionData } from '../models/NotificationActionData';
import { NotificationName, Notifications, NotificationsWrapper } from "../models/Notifications";
import { PaginationModel } from "../models/Pagination";
import { RuleDisplayType, RuleDisplayTypeWrapper } from '../models/RuleDisplayType';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { SettingData } from "../models/SettingData";
import { getDateTimeWithoutMillis } from '../utilities/DateUtils';
import { APIConnector } from './api/APIConnector';
import { setTime } from './utilities/dateFormatting';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { deleteSettingData, saveSettingData } from "./settingDataService";
import { getActiveStatus } from "./utilities/StatusBuilder";

const apiConnector = new APIConnector();

export interface SaveDaily {
    typeSelection: number;
    notificationName: string;
    reportSelection: number;
    startTime: string;
    timeZoneId: string;
    numberOfDaily: string;
    startDate: string;
    emailSubject: string;
    emailBody: string;
    emailDisclaimer: string;
    sendToSelectedIds: string[];
    copyToSelectedIds?: string[];
}

export const getRuleDisplayType = async (listParameters?: ListQuery): Promise<RuleDisplayTypeWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = 'ruleDisplayType?' + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url)
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });;
                }

                const ruleDisplayType: RuleDisplayType[] = data.map((rule) => {
                    return {
                        id: rule['id'],
                        name: rule['vrdt-name'],
                        category: rule['vrdt-category'],
                        nameOriginal: rule['vrdt-name'],

                    }
                })

                return resolve({ rows: ruleDisplayType, pagination });
            });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getVRuleWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = 'ruleDisplayType?' + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url)
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiNotifications: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const notifications: Name[] = apiNotifications.map(rule => {
                    return {
                        label: rule['vrdt-name'],
                        value: rule['id'],
                    }
                });

                const newPagination = {
                    ...pagination,
                    maxPageSize: 8,
                    pageNumber: 1,
                    totalCount: notifications.length,
                    totalPages: 1
                }
                return resolve({ rows: notifications, pagination: newPagination });
            });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const saveDailyNotifications = async (data: SaveDaily) => {
    const raw = {
        type: "notification",
        attributes: {
            name: data.notificationName,
        },
        relationships: {
            vruleDisplayTypeId: +data.typeSelection,
            ruleTriggerGroups: [
                {
                    vruleTriggerId: 1,
                    vrigOrdinal: 1,
                    vrigLogicalJoinOp: "AND",
                    vrigLogicalInnerOp: "OR",
                    settingData: [
                        {
                            eSysnameLookup: "START_TIME",
                            sdValstring: data.startTime,
                        },
                        {
                            eSysnameLookup: "FILTER_TIME_ZONE",
                            sdValstring: data.timeZoneId,
                        },
                        {
                            eSysnameLookup: "PATTERN_EVERY_DAY",
                            sdValstring: data.numberOfDaily,
                        },
                        {
                            eSysnameLookup: "RANGE_START_DATE",
                            sdValstring: data.startDate,
                        },
                    ],
                },
            ],
            ruleActions: [
                {
                    vruleActionTypeId: 1,
                    settingData: [
                        {
                            eSysnameLookup: "REPORT_ID",
                            sdValstring: "12",
                            reportId: 12,
                        },
                        {
                            eSysnameLookup: "MSG_SUBJECT",
                            sdValstring: data.emailSubject,
                        },
                        {
                            eSysnameLookup: "MSG_BODY",
                            sdValstring: data.emailBody,
                        },
                        {
                            eSysnameLookup: "MSG_DISCLAIMER",
                            sdValstring: data.emailDisclaimer,
                        },
                    ],
                },
            ],
        },
    };
    data.sendToSelectedIds.forEach((id, index) => {
        const newRecipient = {
            eSysnameLookup: "TO_RECIPIENT",
            sdValstring: `${id}`,
            masterId: +id,
        };

        raw.relationships.ruleActions[0].settingData.push(newRecipient);
    });
    const url = "";

    if (data.copyToSelectedIds) {
        data.copyToSelectedIds.forEach((id) => {
            const newCopyTo = {
                eSysnameLookup: "CC_RECIPIENT",
                sdValstring: `${id}`,
                masterId: +id,
            };
            raw.relationships.ruleActions[0].settingData.push(newCopyTo);
        });
    }

    await apiConnector
        .sendData(url, true, raw, true)
        .then((data) => {
            if (data.status === 200) {
                return data;
            }
            else {
                return new Promise((resolve, reject) => {
                    return reject({ status: data.status, message: genericError });
                });
            }
        })
        .catch((error) => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            });
        });
};

/*

*/
export const getNotificationNames = async (): Promise<NotificationName[]> => {
    try {
        const url = 'ruleNotification?fields[ruleNotification]=name';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, api: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const results: NotificationName[] = api.map(rule => {
                    return {
                        label: rule['name'],
                        value: rule['id']
                    }
                });
                return resolve(results);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/*

*/
export const getNotificationNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        let filters = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        //filters = filters.replace('location.zoneByZoneId.zoneName', 'zoneName');
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `ruleNotification?${paginationString}&include=vruleDisplayType`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiNotifications: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const notifications: Name[] = apiNotifications.map(rule => {
                    return {
                        label: rule['name'],
                        value: rule['id'],
                        optionalColumns: [{ key: 'type', value: rule["vrule-display-type"]["vrdt-name"] }]
                    }
                });
                return resolve({ rows: notifications, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/*
  Get notifications
*/
export const getNotifications = async (translate: any, listParameters?: ListQuery): Promise<NotificationsWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsNotification = "fields[ruleNotification]=name,vruleDisplayType,vruleAuthor,frequence,time,emailSubject,toRecipientsConcat,reportName,detailsConcat,accountName,vruleEnabled";
        const fieldsAuthor = "fields[userLogin]=ulUsername";
        const fieldsDisplayType = "fields[ruleDisplayType]=trnName";
        const includes = "include=vruleDisplayType,vruleAuthor";
        const url = `ruleNotification?${includes}&${fieldsNotification}&${fieldsDisplayType}&${fieldsAuthor}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const results: Notifications[] = data.map((notification): Notifications => {
                    return {
                        id: notification["id"],
                        name: notification["name"],
                        vruleDisplayType: notification["vrule-display-type"]["trn-name"],
                        frequence: notification["frequence"],
                        time: notification["time"],
                        emailSubject: notification["email-subject"],
                        toRecipientsConcat: notification["to-recipients-concat"],
                        reportName: notification["report-name"],
                        detailsConcat: notification["details-concat"],
                        accountName: notification["account-name"],
                        vruleEnabled: notification['vrule-enabled'] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                        status: getActiveStatus(notification['vrule-enabled']),
                        authorName: notification['vrule-author'] ? notification['vrule-author']['ul-username'] : '',
                    };
                });

                return resolve({ rows: results, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

/*
  Delete the VRule record.
*/
export const deleteNotification = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `rule/${id}`;
        const vrule = {
            data: {
                type: "rule",
                id: id,
            },
        };

        const response = await apiConnector.deleteData(url, vrule);
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeNotificationReportType = async (vruleActionId, reportId): Promise<StoreEntityResult> => {
    const url = `ruleAction/${vruleActionId}`

    const data = {
        data: {
            type: "ruleAction",
            id: vruleActionId,
            relationships: {
                report: {
                    data: {
                        type: "report",
                        id: reportId
                    }
                }
            }
        }
    }

    try {
        const response = await apiConnector.sendData(url, false, data);

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: vruleActionId, success: true })
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: vruleActionId, success: false, message: genericError });
        })
    }
    catch (error) {
        return new Promise((resolve, reject) => {
            return reject(processServerError(error));
        });
    }
}

export const getNotificationById = async (id: string): Promise<Notification> => {
    try {
        const url = `rule/${id}?include=vruleDisplayType,vruleActions.report,vruleActions.account,vruleActions.settingDatas.element,vruleTriggerGroups.settingDatas.element,vruleTriggerGroups.settingDatas.timeZone,vruleTriggerGroups.vruleTrigger`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
              
                const actionData = buildNotificationActionData(data['vrule-actions']);
                const triggerData = buildNotificationTriggerData(data['vrule-trigger-groups']);
                const settingDatasRef = [...actionData.settingDatasRef!, ...triggerData['settingDatasRef']]
                const notification: Notification = {
                    id: data['id'],
                    name: data["vrule-name"],
                    enabled: data["vrule-enabled"],
                    type: data['vrule-display-type']['vrdt-name'],
                    typeId: data['vrule-display-type']['id'],
                    lastTriggered: data["vrule-exec-datetime"]? getDateTimeWithoutMillis(data["vrule-exec-datetime"]) : "",
                    ...actionData,
                    ...triggerData,
                    settingDatasRef
                }

                return resolve(notification);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildNotificationActionData = (vruleActions: any[]): NotificationActionData => {
    const ccRecipients: string[] = [];
    const toRecipients: string[] = [];
    let reportId: string = "", subject: string = "", body: string = "", disclaimer: string = "", vruleActionId: number = 0;
    let report: string = "", settingDatasRef: { id: string, elementSysname: string, sdValstring: string }[] = [];
    let emailAccountId: string = "";
    let emailAccount: string = ""

    vruleActions.forEach(vruleAction => { //Not sure if we will ever have multiple actions but let us be sure
        vruleActionId = vruleAction.id;

        if (vruleAction['report']) {
            reportId = vruleAction['report']['id'];
            report = vruleAction['report']['trn-name']
        }

        if (vruleAction['account']) {
            emailAccountId = vruleAction['account']['id'];
            emailAccount = vruleAction['account']['acc-name']
        }

        vruleAction['setting-datas'].forEach(settingData => { //Evaluate each setting data and assign it where it belong
            const elementSysname = settingData['element']['e-sysname'];
            settingDatasRef.push({
                id: settingData['id'],
                elementSysname,
                sdValstring: settingData['sd-valstring']
            })

            if (elementSysname === TO_RECIPIENT) {
                toRecipients.push(settingData['sd-valstring']);
            }
            else if (elementSysname === CC_RECIPIENT) {
                ccRecipients.push(settingData['sd-valstring']);
            }
            else if (elementSysname === MSG_SUBJECT) {
                subject = settingData['sd-valstring'];
            }
            else if (elementSysname === MSG_BODY) {
                body = settingData['sd-valstring'];
            }
            else if (elementSysname === MSG_DISCLAIMER) {
                disclaimer = settingData['sd-valstring'];
            }
        });
    });

    return {
        vruleActionId,
        emailAccount,
        emailAccountId,
        ccRecipients,
        toRecipients,
        reportId,
        report,
        subject,
        body,
        disclaimer,
        settingDatasRef,
    };
}

const buildNotificationTriggerData = (vruleTriggerGroups: any[]): {} => {
    const daysOfTheWeek: string[] = [];
    let timeZoneId: string = "", timeZone: string = "", startDate: string = "", endDate: string = "";
    let pattern: PATTERN = PATTERN.DAILY, recurrenceOption: RECURRENCE = RECURRENCE.NO_END_DATE;
    let time: string = "", monthOfTheYear: string = "", occurrence: string = "", filterEvents : string [] =[];
    let filterIds:string[] = [], filterBy = "",relationType="";
    let rangeOccurrence: string = "", occurrences: string = "", dayOfMonth: string = "";
    let settingDatasRef: { id: string, elementSysname: string, sdValstring: string }[] = [];
    let vruleTriggerEventId:string = "",vruleTriggerScheduleId:string = "",vruleFilterIds:string = "";
    
    vruleTriggerGroups.forEach((vruleTriggerGroup) => {
        vruleTriggerGroup["setting-datas"].forEach((settingData) => {
            //Evaluate each setting data and assign it where it belong
            const elementSysname = settingData["element"]["e-sysname"];
            settingDatasRef.push({
                id: settingData["id"],
                elementSysname,
                sdValstring: settingData["sd-valstring"],
            });

            if (elementSysname === FILTER_TIME_ZONE) {
                timeZoneId = settingData["sd-valstring"];
                timeZone = settingData["time-zone"]["zone"]; 
            } else if (elementSysname === RANGE_START_DATE) {
                startDate = settingData["sd-valstring"];
            } else if (elementSysname === START_TIME) {
                time = settingData["sd-valstring"];
            } else if (elementSysname === RANGE_END_DATE) {
                endDate = settingData["sd-valstring"];
                recurrenceOption = RECURRENCE.END_BY;
            } else if (elementSysname === RANGE_OCCURRENCE) {
                rangeOccurrence = settingData["sd-valstring"];
                recurrenceOption = RECURRENCE.END_AFTER;
            } else if (elementSysname === PATTERN_EVERY_WEEK) {
                occurrences = settingData["sd-valstring"];
                pattern = PATTERN.WEEKLY;
            } else if (elementSysname === PATTERN_EVERY_DAY) {
                occurrences = settingData["sd-valstring"];
                pattern = PATTERN.DAILY;
            } else if (elementSysname === PATTERN_EVERY_MONTH) {
                occurrences = settingData["sd-valstring"];
                pattern = PATTERN.MONTHLY;
            } else if (elementSysname === PATTERN_EVERY_YEAR) {
                occurrences = settingData["sd-valstring"];
                pattern = PATTERN.YEARLY;
            } else if (elementSysname === PATTERN_DAY_OF_MONTH) {
                dayOfMonth = settingData["sd-valstring"];
            } else if (elementSysname === PATTERN_DAY_OF_WEEK) {
                daysOfTheWeek.push(settingData["sd-valstring"]);
            } else if (elementSysname === PATTERN_DAY_TYPE) {
                occurrence = settingData["sd-valstring"];
            } else if (elementSysname === PATTERN_MONTH_OF_YEAR) {
                monthOfTheYear = settingData["sd-valstring"];
            } else if (elementSysname === FILTER_EVENTS) {
                filterEvents.push(settingData["sd-valstring"]);
            } else if (elementSysname === FILTER_DOORS.eSysnameLookup) {
                filterIds.push(settingData["sd-valstring"]);
            } else if (elementSysname === FILTER_SITES.eSysnameLookup) {
                filterIds.push(settingData["sd-valstring"]);
            } else if (elementSysname === FILTER_BUILDINGS.eSysnameLookup) {
                filterIds.push(settingData["sd-valstring"]);
            } else if (elementSysname === FILTER_ZONES.eSysnameLookup) {
                filterIds.push(settingData["sd-valstring"]);
            } else if (elementSysname === FILTER_DEVICE_TYPE.eSysnameLookup) {
                filterIds.push(settingData["sd-valstring"]);
            }
        });

        //Setting up information for triggers
        const vruleTriggerGroupId: string = vruleTriggerGroup["id"];
        const vruleTriggerId: string = vruleTriggerGroup["vrule-trigger"]["id"];

        if (vruleTriggerId === VRULE_TRIGGERS_IDS.EVENT) {
            vruleTriggerEventId = vruleTriggerGroupId;
        }
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.SCHEDULE) {
            vruleTriggerScheduleId = vruleTriggerGroupId;
        } 
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.BUILDING) {
            vruleFilterIds = vruleTriggerGroupId;
            filterBy = FILTER_BUILDINGS.eSysnameLookup;
            relationType = FILTER_BUILDINGS.relationType;
        }
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.SITE) {
            vruleFilterIds = vruleTriggerGroupId;
            filterBy = FILTER_SITES.eSysnameLookup;
            relationType = FILTER_SITES.relationType;
        }
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.ZONE) {
            vruleFilterIds = vruleTriggerGroupId;
            filterBy = FILTER_ZONES.eSysnameLookup;
            relationType = FILTER_ZONES.relationType;
        }
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.DEVICE_TYPE) {
            vruleFilterIds = vruleTriggerGroupId;
            filterBy = FILTER_DEVICE_TYPE.eSysnameLookup;
            relationType = FILTER_DEVICE_TYPE.relationType;
        }
        else if (vruleTriggerId === VRULE_TRIGGERS_IDS.LOCATION) {
            vruleFilterIds = vruleTriggerGroupId;
            filterBy = FILTER_DOORS.eSysnameLookup;
            relationType = FILTER_DOORS.relationType;
        }
    });

    let commonData = {
        pattern,
        time: setTime(time),
        timeZone,
        timeZoneId,
        startDate: startDate ? new Date(startDate) : "",
        occurrences,
        endDate: endDate ? new Date(endDate) : "",
        rangeOccurrence,
        recurrenceOption,
        settingDatasRef,
        filterEvents,
        filterBy,
        filterIds,
        vruleTriggerEventId, 
        vruleTriggerScheduleId,
        vruleFilterIds,
        relationType
    }

    //Let make sure we only return things we are interested on based on the pattern selected
    if (pattern === PATTERN.DAILY) {
        return daysOfTheWeek.length > 0 ?
            { ...commonData, daysOfTheWeek, dailyOption: DAILY_OPTION.EVERY_WEEKDAY } :
            { ...commonData, dailyOption: DAILY_OPTION.EVERY };
    }
    else if (pattern === PATTERN.WEEKLY) {
        return { ...commonData, daysOfTheWeek };
    }
    else if (pattern === PATTERN.MONTHLY) {
        const day: string = daysOfTheWeek.length > 0 ? daysOfTheWeek[0] : "";
        return day === "" ?
            { ...commonData, dayOfMonth, monthlyOption: MONTHLY_OPTION.DAY } :
            { ...commonData, dayOfMonth, occurrence, daysOfTheWeek: day, monthlyOption: MONTHLY_OPTION.THE }
    }
    else if (pattern === PATTERN.YEARLY) {
        const day: string = daysOfTheWeek.length > 0 ? daysOfTheWeek[0] : "";
        return day === "" ?
            { ...commonData, dayOfMonth, monthOfTheYear, yearlyOption: YEARLY_OPTION.ON } :
            { ...commonData, occurrence, daysOfTheWeek: day, monthOfTheYear, yearlyOption: YEARLY_OPTION.ON_THE }
    }

    return commonData;
}

export const saveEventBaseNotification = async ( values: any): Promise<StoreEntityResult> => {
    try {
        const data = {
            type: "notification",
            attributes: {
                name: values.name,
            },

            relationships: {
                vruleDisplayTypeId: 2,
                ruleTriggerGroups: [
                    {
                        vruleTriggerId: 2,
                        vrigOrdinal: 1,
                        vrigLogicalJoinOp: "AND",
                        vrigLogicalInnerOp: "OR",
                        settingData: prepareEventNotificationSettingData(
                            values.eventSelectionId,
                            FILTER_EVENTS
                        ),
                    },
                    {
                        vruleTriggerId: parseInt(values.filterById),
                        vrigOrdinal: 2,
                        vrigLogicalJoinOp: "AND",
                        vrigLogicalInnerOp: "OR",
                        settingData: prepareEventNotificationSettingData(
                            values.configurationId,
                            values.filterBy
                        ), 
                    },
                ],
                ruleActions: [
                    {
                        vruleActionTypeId: 1,
                        accountId: values.emailAccountId,
                        settingData: [
                            {
                                eSysnameLookup: MSG_SUBJECT,
                                sdValstring: values.subject
                            },
                            {
                                eSysnameLookup: MSG_BODY,
                                sdValstring: values.body
                            },
                            {
                                eSysnameLookup: MSG_DISCLAIMER,
                                sdValstring: values.disclaimer
                            },
                            ...buildRecipientsToSave(values.ccRecipients, CC_RECIPIENT),
                            ...buildRecipientsToSave(values.toRecipients, TO_RECIPIENT),
                        ]
                    }
                ]
            }
        };

        return saveNotification(data);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const prepareEventNotificationSettingData = (events:string[],elementSysname) => {
    const relationshipsElements = [
        {elementSysname:FILTER_DOORS.eSysnameLookup,relationshipName:"locationId"},
        {elementSysname:FILTER_SITES.eSysnameLookup,relationshipName:"siteId"},
        {elementSysname:FILTER_BUILDINGS.eSysnameLookup,relationshipName:"buildingId"},
        {elementSysname:FILTER_ZONES.eSysnameLookup,relationshipName:"zoneId"},
        {elementSysname:FILTER_DEVICE_TYPE.eSysnameLookup,relationshipName:"deviceImproxId"},
        {elementSysname:FILTER_EVENTS,relationshipName:"eventTypeId"},
    ]
    const relationShipElementLookUp = relationshipsElements.find((element) => element.elementSysname === elementSysname);

    if(!relationShipElementLookUp){
        return []
    }

    return events.map((event) => {
        return{
            eSysnameLookup: elementSysname,
            sdValstring: event,
            [relationShipElementLookUp?.relationshipName]: event
        }
    })
}

export const saveScheduledNotification = async (values: any): Promise<StoreEntityResult> => {
    try {
        const data = {
            type: "notification",
            attributes: {
                name: values.name
            },
            relationships: {
                vruleDisplayTypeId: 1,
                ruleTriggerGroups: [
                    {
                        vruleTriggerId: 1,
                        vrigOrdinal: 1,
                        vrigLogicalJoinOp: "AND",
                        vrigLogicalInnerOp: "OR",
                        settingData: [
                            {
                                eSysnameLookup: START_TIME,
                                sdValstring: values.time
                            },
                            {
                                eSysnameLookup: FILTER_TIME_ZONE,
                                sdValstring: values.timeZoneId,
                                timeZoneId: values.timeZoneId
                            },
                            {
                                eSysnameLookup: RANGE_START_DATE,
                                sdValstring: values.startDate
                            },
                            ...buildPatternData(values),
                            ...buildRecurrence(values.recurrenceOption, values.endDate, values.rangeOccurrence)
                        ]
                    }
                ],
                ruleActions: [
                    {
                        vruleActionTypeId: 1,
                        reportId: values.reportId,
                        accountId: values.emailAccountId,
                        settingData: [
                            {
                                eSysnameLookup: MSG_SUBJECT,
                                sdValstring: values.subject
                            },
                            {
                                eSysnameLookup: MSG_BODY,
                                sdValstring: values.body
                            },
                            {
                                eSysnameLookup: MSG_DISCLAIMER,
                                sdValstring: values.disclaimer
                            },
                            ...buildRecipientsToSave(values.ccRecipients, CC_RECIPIENT),
                            ...buildRecipientsToSave(values.toRecipients, TO_RECIPIENT),
                        ]
                    }
                ]
            }
        }


        return  saveNotification(data)
    }
    catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const saveNotification = async (data: any):Promise<StoreEntityResult> => {
    let response: Response;

        await apiConnector.sendData("", true, data, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response.status === CREATED || response.status === OK) {
                return resolve({ id: 1, success: true })
            }

            const content = await response.json();
            return reject({ status: response.status, id: 1, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
        });
}

const buildRecipientsToSave = (recipients: string[], elementSysname: string) => {
    return recipients.map((masterId) => {
        return {
            eSysnameLookup: elementSysname,
            sdValstring: masterId,
            masterId: masterId
        }
    });
}

const buildPatternData = (values) => {
    //Daily
    if (values.pattern === PATTERN.DAILY) {
        //Every weekday
        if (values.dailyOption === DAILY_OPTION.EVERY_WEEKDAY) {
            const everyWeekdayDefaultValue = "1"
            const data: {}[] = [...buildPattern(values.pattern, everyWeekdayDefaultValue),]
            const daysOfTheWeek = buildDaysOfTheWeekData(values.daysOfTheWeek);
            return [...data, ...daysOfTheWeek]
        }
        //Every
        const data: {}[] = [...buildPattern(values.pattern, values.occurrences),]
        return data;
    }
    //Weekly
    else if (values.pattern === PATTERN.WEEKLY) {
        const pattern = buildPattern(values.pattern, values.occurrences);
        return [
            ...pattern,
            ...buildDaysOfTheWeekData(values.daysOfTheWeek)
        ]
    }
    //Monthly
    else if (values.pattern === PATTERN.MONTHLY) {
        //The
        if (values.monthlyOption === MONTHLY_OPTION.THE) {
            const data: {}[] = [...buildPattern(values.pattern, values.occurrences)];
            return [
                ...data,
                ...buildDaysOfTheWeekData(values.daysOfTheWeek),
                {
                    eSysnameLookup: PATTERN_DAY_TYPE,
                    sdValstring: values.occurrence
                }
            ];
        }
        //Day
        const data: {}[] = [
            ...buildPattern(values.pattern, values.occurrences),
            {
                eSysnameLookup: PATTERN_DAY_OF_MONTH,
                sdValstring: values.dayOfMonth
            }
        ]
        return data;
    }
    //Yearly
    else if (values.pattern === PATTERN.YEARLY) {
        const data: {}[] = [
            ...buildPattern(values.pattern, values.occurrences),
            {
                eSysnameLookup: PATTERN_MONTH_OF_YEAR,
                sdValstring: values.monthOfTheYear
            }
        ];
        //On the
        if (values.yearlyOption === YEARLY_OPTION.ON_THE) {
            return [
                ...data,
                ...buildDaysOfTheWeekData(values.daysOfTheWeek),
                {
                    eSysnameLookup: PATTERN_DAY_TYPE,
                    sdValstring: values.occurrence
                }
            ];
        }
        //On
        return [
            ...data,
            {
                eSysnameLookup: PATTERN_DAY_OF_MONTH,
                sdValstring: values.dayOfMonth
            }
        ]
    }

    return [];
}

const buildDaysOfTheWeekData = (daysOfTheWeek: string[] | string) => {
    if (typeof daysOfTheWeek === "string") {
        return [{
            eSysnameLookup: PATTERN_DAY_OF_WEEK,
            sdValstring: daysOfTheWeek
        }]
    }

    return daysOfTheWeek.map((day: string) => {
        return {
            eSysnameLookup: PATTERN_DAY_OF_WEEK,
            sdValstring: day
        }
    })
}

const buildPattern = (pattern: string, occurrences: string) => {
    const patterns: { key: string, elementSysname: string }[] = [
        { key: PATTERN.DAILY, elementSysname: PATTERN_EVERY_DAY },
        { key: PATTERN.WEEKLY, elementSysname: PATTERN_EVERY_WEEK },
        { key: PATTERN.MONTHLY, elementSysname: PATTERN_EVERY_MONTH },
        { key: PATTERN.YEARLY, elementSysname: PATTERN_EVERY_YEAR },
    ];

    const elementSysname: string | undefined = patterns.find((value) => pattern === value.key)?.elementSysname;

    if (elementSysname) {
        return [{
            eSysnameLookup: elementSysname,
            sdValstring: occurrences
        }]
    }

    return [];
}

const buildRecurrence = (recurrenceOption: string, endDate: string, rangeOccurrence: string) => {
    if (recurrenceOption === RECURRENCE.END_BY) {
        return [{
            eSysnameLookup: RANGE_END_DATE,
            sdValstring: endDate
        }];
    }
    else if (recurrenceOption === RECURRENCE.END_AFTER) {
        return [{
            eSysnameLookup: RANGE_OCCURRENCE,
            sdValstring: rangeOccurrence
        }];
    }

    return [];
}

export const editNotification = async (
    settingDataToRemove: string[],
    settingDataToAdd: SettingData[],
    settingDataToEdit: SettingData[],
    notificationId: number,
    vruleActionId: number,
    notificationName?: string,
    emailAccountId?: string,
    reportId?: string,
    enabled?: number): Promise<{ success: boolean, message: string }> => {

    const allPromisesToAwait: Promise<{ id: string, success: boolean }>[] = [];

    if(reportId) {
        const updateReportIdPromise = storeNotificationReportType(vruleActionId, reportId);
        allPromisesToAwait.push(updateReportIdPromise);
    }

    //Update rule attribute vruleName
    if (notificationName !== undefined || enabled !== undefined) {
        const updatedNotificationName = notificationName !== undefined ? notificationName : undefined;
        const updatedEnabled = enabled !== undefined ? enabled : undefined;

        const updateVrulePromise = editVruleInfo(notificationId, updatedNotificationName, updatedEnabled);
        allPromisesToAwait.push(updateVrulePromise);
    }

    // update emailAccount id in vrule actions
    if (emailAccountId) {
        const updateEmailAccountIdPromise = editNotificationAccount(emailAccountId, vruleActionId);
        allPromisesToAwait.push(updateEmailAccountIdPromise);
    }

    //Remove setting data
    if (settingDataToRemove) {
        const deletePromises = settingDataToRemove.map(settingData => deleteSettingData(settingData));
        allPromisesToAwait.push(...deletePromises);
    }

    //Add setting data
    if (settingDataToAdd) {
        const createPromises: Promise<any>[] = settingDataToAdd.map(settingData => saveSettingData(
           settingData
        ));
        allPromisesToAwait.push(...createPromises);
    }

    //Edit setting data
    if (settingDataToEdit) {
        const editPromises: Promise<any>[] = settingDataToEdit.map(settingData => saveSettingData(settingData))
        allPromisesToAwait.push(...editPromises)
    }

    //After all this is done return a single promise based on what happened above
    try {
        const successResults = await Promise.all(allPromisesToAwait);

        return new Promise((resolve, reject) => {
            return resolve({ success: true, message: "Edit Successful" });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ success: false, message: "Edit Failed" })
        })
    }
}

const editVruleInfo = async (notificationId: number, notificationName?: string, enabled?: number): Promise<{ id: string, success: boolean }> => {
    try {
        
        const updateRecord = {
            data: {
                type: "rule",
                id: notificationId,
                attributes: {
                    vruleName: notificationName && notificationName,
                    vruleEnabled: enabled && enabled
                }
            },
        };

        const url = `rule/${notificationId}`;
        const response = await apiConnector.sendData(url, false, updateRecord)

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: notificationId, success: true })
                }

                const json = await response.json();
                const id = notificationId;
                return reject({ status: response.status, id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: notificationId, success: false });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const editNotificationAccount = async (emailAccountId: string, vruleActionId: number): Promise<{ id: string, success: boolean }> => {
    try {
        const updateRecord = {
            data: {
                type: "ruleAction",
                id: vruleActionId,
                relationships: {
                    account: {
                        data: {
                            id: emailAccountId,
                            type: "account"
                        }
                    }
                }
            },
        };

        const url = `ruleAction/${vruleActionId}`;
        const response = await apiConnector.sendData(url, false, updateRecord)

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: vruleActionId, success: true })
                }

                const json = await response.json();
                return reject({ status: response.status, id: vruleActionId, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: vruleActionId, success: false });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const handleManage = async (settingDataToAdd: SettingData[], settingDataToRemove: string[]): Promise<unknown> => {
    const allPromisesToAwait: Promise<{ id: string, success: boolean }>[] = [];

    if (settingDataToAdd) {
        const addPromises: Promise<any>[] = settingDataToAdd.map(settingData => saveSettingData(settingData))
        allPromisesToAwait.push(...addPromises);
    }

    if (settingDataToRemove) {
        const deletePromises = settingDataToRemove.map(settingData => deleteSettingData(settingData));
        allPromisesToAwait.push(...deletePromises);
    }

    try {
        return await Promise.allSettled(allPromisesToAwait);
    } catch (error) {
        return new Promise((_, reject) => {
            reject(processServerError(error))
        })
    }
}

export const getEventBasedFilteredByData = (translate: Translator): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const FILTER_BY_OPTIONS = [
            {value: FILTER_SITES, label: translate.byKey(TranslationKeys.site)},
            {value: FILTER_BUILDINGS, label: translate.byKey(TranslationKeys.building)},
            {value: FILTER_ZONES, label: translate.byKey(TranslationKeys.zone)},
            {value: FILTER_DOORS, label: translate.byKey(TranslationKeys.door)},
            {value: FILTER_DEVICE_TYPE, label: translate.byKey(TranslationKeys.device)},
        ]
        return new Promise((resolve, reject) => {
            const options: Name[] = FILTER_BY_OPTIONS.map(data => {
                return {
                    label: data.label,
                    value: data.value.sdValstring,
                    name: data.label,
                    filterValue: data.value.eSysnameLookup
                }
            });

            const newPagination = {
                maxPageSize: 8,
                pageNumber: 1,
                totalCount: options.length,
                totalPages: 1
            }
            return resolve({ rows: options, pagination: newPagination });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}